import React from 'react';
import { ProductCardProps } from '@/types/product';
import { ShoppingCart, Package, Zap, Lock, Tag } from 'lucide-react';
import { CoinIcon } from "@/app/components/SvgIcons";
import Image from 'next/image';

export default function ProductCard({ product, onBuyNow, user }: ProductCardProps) {
    const displayPrice = product.valueDisplay;
    const isKickUser = user?.provider === 'kick';
    const isOutOfStock = product.quantity === 0;
    const isDiscounted = product.discount_percent > 0;

    return (
        <div className="relative backdrop-blur rounded-2xl overflow-hidden transition-all duration-300 group h-full">
            {/* Subtle glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-green-400/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

            <div className="relative z-10 h-full flex flex-col">

                {/* Badge */}
                {product.badge && (
                    <div className="absolute top-2 left-2 z-10">
                        <span className="inline-flex items-center gap-1 bg-green-500/10 text-green-400 text-xs font-mono font-bold px-3 py-1.5 rounded-full">
                            {product.virtual ? <Zap className="w-3 h-3" /> : <Package className="w-3 h-3" />}
                            {product.badge}
                        </span>
                    </div>
                )}

                {/* Discount label */}
                {isDiscounted && (
                    <div className="absolute top-2 right-2 z-10">
                        <span className="inline-flex items-center gap-1 bg-red-500/10 text-red-400 text-xs font-mono font-bold px-3 py-1.5 rounded-full">
                            <Tag className="w-3 h-3" />
                            -{product.discount_percent}%
                        </span>
                    </div>
                )}

                {/* Image Section with Zoom Effect */}
                <div className={`relative w-full h-56 overflow-hidden bg-gray-900 ${isOutOfStock ? 'opacity-50' : ''}`}>
                    {product.image_url ? (
                        <div className="w-full h-full flex items-center justify-center p-4">
                            <Image
                                src={product.image_url}
                                alt={product.title}
                                width={150}
                                height={150}
                                className="max-w-full max-h-full object-contain transition-transform duration-500 ease-out group-hover:scale-110"
                            />
                        </div>
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-600">
                            <Package className="w-20 h-20 transition-transform duration-500 ease-out group-hover:scale-110" />
                        </div>
                    )}
                </div>

                {/* Content Section */}
                <div className="p-6 flex flex-col flex-grow">
                    {/* Fixed 2-line title height so every card lines up */}
                    <h3 className="uppercase text-xl leading-7 font-bold text-white mb-2 line-clamp-2 min-h-[3.5rem]" title={product.title}>
                        {product.title}
                    </h3>

                    {/* Stock Info */}
                    <div className="flex items-center gap-2 mb-4 text-xs">
                        <div
                            className={`px-2 py-1 rounded-md ${product.quantity > 10
                                ? 'bg-green-900/30 text-green-400 border border-green-800'
                                : product.quantity > 0
                                    ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-800'
                                    : 'bg-red-900/30 text-red-400 border border-red-800'
                                }`}
                        >
                            {product.quantity > 0 ? `${product.quantity} in stock` : 'Out of stock'}
                        </div>
                    </div>

                    {/* Fixed 3-line description; full text is shown in the purchase modal */}
                    <p className="text-gray-400 text-sm leading-5 line-clamp-3 h-[3.75rem]" title={product.description || undefined}>
                        {product.description || 'No description available'}
                    </p>
                    <div className="flex-grow min-h-4" />

                    {/* Price and Buy Button */}
                    <div className="flex flex-col pt-4 border-t border-green-400/20">
                        {/* Price Row */}
                        <div className="flex items-center justify-center gap-3 mb-4">
                            {isDiscounted && (
                                <span className="text-base font-mono text-gray-500 line-through">
                                    {product.originalValueDisplay}
                                </span>
                            )}
                            <span className={`text-2xl font-mono flex items-center gap-1 ${isDiscounted ? 'text-green-400 font-bold' : 'text-gray-200'}`}>
                                {displayPrice}
                                <CoinIcon size={20} />
                            </span>
                        </div>

                        {/* Button Logic */}
                        {!isKickUser ? (
                            // Not a Kick user
                            <div className="w-full px-4 py-3 rounded-lg font-bold text-sm bg-gray-800 text-gray-400 flex items-center justify-center gap-2 cursor-not-allowed">
                                <Lock className="w-4 h-4" />
                                KICK USERS ONLY
                            </div>
                        ) : isOutOfStock ? (
                            // Kick user but out of stock
                            <div className="w-full px-4 py-3 rounded-lg font-bold text-sm bg-gray-800 text-gray-400 flex items-center justify-center gap-2 cursor-not-allowed">
                                <Lock className="w-4 h-4" />
                                OUT OF STOCK
                            </div>
                        ) : (
                            // Kick user and in stock
                            <button
                                onClick={() => onBuyNow(product)}
                                className="w-full px-4 py-3 rounded-lg font-bold text-sm transition-all duration-200 bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 shadow-lg hover:shadow-emerald-500/30 relative overflow-hidden"
                            >
                                <span className="relative z-10 flex items-center justify-center gap-2">
                                    <ShoppingCart className="w-4 h-4" />
                                    BUY NOW
                                </span>
                                <div className="absolute inset-0 bg-gradient-to-r from-green-400/0 via-green-400/30 to-green-400/0 transform -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
